import React, { useRef, useEffect, useState, useContext } from "react";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";
import CiteModal from "./CiteModal";
import { AppContext } from "../../../../../services/AuthContext";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PdfAcrobatIcon from "./PdfAcrobatIcon";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { buildAuthorPath, buildPublicationPath, buildPublisherPath } from "../../../../../utils/slugify";

import {
  generateAPA,
  generateMLA,
  generateChicago,
  generateBibTeX,
  generateVancouver,
  generateHarvard,
  generateIEEE
  // ... other formats
} from "./utils/citationFormats";

// ---------- helpers ----------
const languageNamesInTurkish = {
  tr: "Türkçe",
  en: "İngilizce",
  ar: "Arapça",
  fr: "Fransızca",
  de: "Almanca",
  es: "İspanyolca",
  pt: "Portekizce",
  ru: "Rusça",
  zh: "Çince",
  ja: "Japonca",
  it: "İtalyanca",
  nl: "Hollandaca",
  // gerekiyorsa ekle
  other: "İkinci"
};

function normalizeLangCode(lang) {
  if (!lang) return "";
  return String(lang).split(/[-_]/)[0].toLowerCase(); // 'en-US' -> 'en'
}


const getTitle = (pub) => {
  const t = pub.publicationtypeInfo?.find(attr => attr.attributename === "Başlık")?.value?.[0]?.value?.[0] ||
    pub.templateInfo?.find(attr => attr.attributename === "Başlık")?.value?.[0]?.value?.[0] ||
    "Başlık Yok";
  return typeof t === 'string' ? t.replace(/\s*\/$/, '').trim() : t;
};

const getSecondTitle = (pub) => {
  const t = pub.publicationtypeInfo?.find(attr => attr.attributename === "Başlık")?.value?.[1]?.value?.[0] ||
    pub.templateInfo?.find(attr => attr.attributename === "Başlık")?.value?.[1]?.value?.[0] ||
    "İkinci Başlık Yok";
  return typeof t === 'string' ? t.replace(/\s*\/$/, '').trim() : t;
};

const getJournal = (pub) => {
  const t = pub.parentList?.[0]?.publicationtypeInfo?.find(attr => attr.attributename === "Dergi Adı")?.value?.[0]?.value?.[0] ||
    pub.parentList?.[0]?.publicationtypeInfo?.find(attr => attr.attributename === "Başlık")?.value?.[0]?.value?.[0] ||
    "";
  return typeof t === 'string' ? t.replace(/\s*\/$/, '').trim() : t;
};

const getYear = (pub) =>
  pub.templateInfo?.find(attr => attr.attributename === 'Yayın Tarihi')?.value?.[0]?.value?.[0]?.split(" ")?.[0] ||
  pub.publicationtypeInfo?.find(attr => attr.attributename === 'Yayın Tarihi')?.value?.[0]?.value?.[0]?.split(" ")?.[0] ||
  "";

const getTags = (pub) => {
  const extractAttrArr = (list, attrName) => {
    if (!Array.isArray(list)) return [];
    const attr = list.find(a => a.attributename === attrName);
    if (!attr || !attr.value) return [];
    if (Array.isArray(attr.value)) {
      const trVal = attr.value.find(v => v.lang === "tr" && Array.isArray(v.value))?.value;
      if (trVal) return trVal;
      const firstVal = attr.value.find(v => Array.isArray(v.value))?.value;
      if (firstVal) return firstVal;
      if (typeof attr.value[0] === 'string') return attr.value;
    }
    return [];
  };

  const pubTypeTopics = extractAttrArr(pub?.publicationtypeInfo, "Konu Başlıkları");
  const pubTypeKeywords = extractAttrArr(pub?.publicationtypeInfo, "Anahtar Kelimeler");
  const templateTopics = extractAttrArr(pub?.templateInfo, "Konu Başlıkları");
  const templateKeywords = extractAttrArr(pub?.templateInfo, "Anahtar Kelimeler");

  const all = [
    ...pubTypeTopics,
    ...templateTopics,
    ...pubTypeKeywords,
    ...templateKeywords,
  ]
    .map(x => (typeof x === "string" ? x.trim() : String(x).trim()))
    .filter(Boolean);

  return Array.from(new Set(all));
};

const getTypeLabel = (pub) => pub.publicationtype || "";

// Display-only normalization: remove academic/popular prefixes
function normalizeTypeDisplay(type) {
  if (!type) return "";
  return String(type)
    .replace(/^\s*/, '')
    .replace(/^(akademik\s+|popüler\s+|populer\s+)/i, '')
    .trim();
}

// any date-ish string → only year(YYYY)
function getYearOnly(value) {
  if (!value) return "";
  const yearMatch = String(value).match(/\d{4}/);
  return yearMatch ? yearMatch[0] : "";
}

const getFrom = (name, arr = []) =>
  arr?.find(a => a.attributename === name)?.value?.[0]?.value?.[0] || "";

const first = (...vals) => vals.find(Boolean) || "";

// job normalizasyonu
function normalizeJob(job) {
  const j = String(job || "").trim().toLowerCase();
  if (j === "yazar" || j === "author") return "yazar";
  if (j === "editör" || j === "editor" || j === "editör " || j === "editor ") return "editör";
  return "";
}

// get first 'Cilt' value from publicationtypeInfo or templateInfo (if present)
function getCiltValue(pub) {
  if (!pub) return "";
  const ciltFromPub = getFrom("Cilt", pub.publicationtypeInfo || []);
  const ciltFromTemplate = getFrom("Cilt", pub.templateInfo || []);
  return first(ciltFromPub, ciltFromTemplate) || "";
}

// Determine whether we should show (Cilt X) next to title for given publication types
function shouldShowCilt(pub) {
  if (!pub || !pub.publicationtype) return false;
  const t = String(pub.publicationtype).toLocaleLowerCase("tr-TR");
  const targetTypes = [
    "konferans ve armağan",
    "konferans ve armagan",
    "popüler kitap",
    "populer kitap",
    "akademik kitap"
  ];
  return targetTypes.some(tt => t.includes(tt));
}

// Kartta gösterilecek kişileri (yazar > yoksa editör (ed.)) üretir
function buildContributors(pub) {
  const list = Array.isArray(pub?.authors) ? pub.authors : [];
  if (!list.length) return { persons: [], mode: "none" };

  // Duplicates önlemek için id veya ad-soyad ile uniq
  const uniqMap = new Map();
  list.forEach(p => {
    const key = p?.mysqlId ? `id:${p.mysqlId}` : `n:${(p?.name || "").trim()}|${(p?.surname || "").trim()}`;
    if (!uniqMap.has(key)) uniqMap.set(key, p);
  });
  const uniq = [...uniqMap.values()];

  const authors = uniq.filter(p => normalizeJob(p.job) === "yazar");
  const editors = uniq.filter(p => normalizeJob(p.job) === "editör");

  if (authors.length) return { persons: authors, mode: "author" };
  if (editors.length) return { persons: editors, mode: "editor" };
  // "Hazırlayan" yoksa şimdilik boş (birazdan ekleyeceğiz)
  return { persons: [], mode: "none" };
}

export function getSecondTitleFieldLocalized(pub) {
  // bul: publicationtypeInfo veya templateInfo içindeki "Başlık" attribute'u
  const attr =
    pub.publicationtypeInfo?.find(a => a.attributename === "Başlık") ||
    pub.templateInfo?.find(a => a.attributename === "Başlık");

  if (!attr || !Array.isArray(attr.value)) return null;

  // mevcut mantığı bozulmasın diye sadece 2. elemanı kullanıyoruz
  const secondEntry = attr.value[1]; // burada mevcut mantığınla aynı
  if (!secondEntry || !Array.isArray(secondEntry.value) || !secondEntry.value[0]) return null;

  const lang = normalizeLangCode(secondEntry.lang) || "tr"; // fallback tr
  const langName = languageNamesInTurkish[lang] || languageNamesInTurkish["other"];

  return {
    label: `${langName} Başlık`,
    value: String(secondEntry.value[0]).trim()
  };
}


// --------------------------------

const PublicationCard = ({
  pub,
  selected,
  onSelect,
  onDetails,
  onCite,
  onAddToLibrary,
  onKeywordClick,
  favoriteSavedIds = [],
  userinstDatabases = [],
  isSummaryOpen = false,
  onToggleSummary,
  onRemoveSaved,
}) => {
  const title = getTitle(pub);
  const authors = pub.authors || [];
  const journal = getJournal(pub);
  const year = getYear(pub);
  const tags = getTags(pub);
  const type = getTypeLabel(pub);
  const ciltValue = getCiltValue(pub);
  const showCilt = shouldShowCilt(pub) && Boolean(ciltValue);
  const [showJournalInfo, setShowJournalInfo] = React.useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const journalInfoRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [selectedPublication, setSelectedPublication] = useState(null);
  const SUMMARY_MAX_PX = 180;
  const { auth } = useContext(AppContext);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const saved = favoriteSavedIds.includes(String(pub.mysqlId));
  const [isSaveHovered, setIsSaveHovered] = useState(false);
  const { persons, mode } = buildContributors(pub);

  //alttakini sil bunu uncommentlersen açık erişim her zaman unlocked olur
  // yeni (daha dayanıklı):
  const isOpenAccess =
    pub?.openaccess === 1 ||
    pub?.openaccess === "1" ||
    pub?.openaccess === true;

  const hasInstitutionAccess =
    Array.isArray(pub?.databases) &&
    pub.databases.some(db => {
      const name = db?.name;
      const id = db?.mysqlId ?? db?.id;
      return (name && userinstDatabases.includes(name)) ||
        (id && userinstDatabases.includes(String(id)));
    });

  const isAccessible = isOpenAccess || hasInstitutionAccess;

  // const isAccessible = pub.databases?.some(db => userinstDatabases.includes(db.name));

  const openInNewTab = (url) => window.open(url, "_blank", "noopener,noreferrer");



  const htmlToPlain = (html) => {
    if (!html) return "";
    return String(html)
      .replace(/<\s*br\s*\/?>/gi, "")
      .replace(/<\s*\/\s*p\s*>/gi, "")
      .replace(/<\s*p\b[^>]*>/gi, "");
  };

  const citationFormats = {
    apa: generateAPA,
    mla: generateMLA,
    chicago: generateChicago,
    vancouver: generateVancouver,
    bibtex: generateBibTeX,
    harvard: generateHarvard,
    ieee: generateIEEE
  };

  const handleOpenModal = (publication) => {
    setSelectedPublication(publication);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFormat('');
  };

  const renderCitation = () => {
    if (!selectedPublication || !selectedFormat) return 'Please select a format.';
    return citationFormats[selectedFormat](selectedPublication);
  };

  function toTitleCase(str) {
    if (!str) return "";
    return str.split(" ")
      .map(word => word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1))
      .join(" ");
  }

  useEffect(() => {
    if (isSummaryOpen && journalInfoRef.current) {
      setMaxHeight(journalInfoRef.current.scrollHeight);
    } else {
      setMaxHeight(0);
    }
  }, [isSummaryOpen, pub]);

  const isRestricted = pub.openaccess === 0;
  const navigate = useNavigate();
  const pdfLink = pub.pdffile
    ? (pub.openaccess === 1
      ? `/view-pdf/${pub.mysqlId}`
      : `/view-pdf-protectedaccess/${pub.mysqlId}`)
    : null;



  const titleUrl = buildPublicationPath({ title: getTitle(pub), id: pub.mysqlId });

  const handleSummaryWheel = (e) => {
    const el = e.currentTarget;
    const atTop = el.scrollTop === 0;
    const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
    if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // ---- Detail panel: type-specific summary fields (like "kitap" style for konf/armağan) ----
  function getSummaryFields(pub) {
    const type = pub.publicationtype?.toLowerCase();
    const info = pub.publicationtypeInfo || [];
    const parent0 = pub.parentList?.[0]?.publicationtypeInfo || [];
    const parent1 = pub.parentList?.[1]?.publicationtypeInfo || [];
    const webUrl = getFrom("Web Adresi", pub.templateInfo || []);

    // Sadece ikinci başlık gerçekten varsa göster
    const secondTitleField = getSecondTitleFieldLocalized(pub);

    // Makale
    if (type.includes("makale")) {
      const doiVal = first(getFrom("DOI", info), getFrom("DOI", parent0), getFrom("DOI", parent1));
      return [
        doiVal ? { label: "DOI", value: `doi.org/${doiVal}` } : null,
        { label: "ISSN", value: first(getFrom("ISSN", parent0), getFrom("ISSN", parent1), getFrom("ISSN", info)) },
        webUrl ? { label: "Web Adresi", value: webUrl } : null,
        // secondTitleField // Sadece varsa eklenecek
      ].filter(x => x && x.value);
    }

    // Kitap
    if (type.includes("kitap") && !type.includes("bölüm")) {
      const doiVal = first(getFrom("DOI", info), getFrom("DOI", parent0));
      return [
        { label: "ISBN", value: first(getFrom("ISBN", info), getFrom("ISBN", parent0)) },
        doiVal ? { label: "DOI", value: `doi.org/${doiVal}` } : null,
        { label: "E-ISBN", value: first(getFrom("E-ISBN", info), getFrom("E-ISBN", parent0)) },
        webUrl ? { label: "Web Adresi", value: webUrl } : null,
        // secondTitleField // Sadece varsa eklenecek
      ].filter(x => x && x.value);
    }

    // Kitap Bölümü
    if (type.includes("bölüm")) {
      const doiVal = first(getFrom("DOI", info), getFrom("DOI", parent0));
      return [
        doiVal ? { label: "DOI", value: `doi.org/${doiVal}` } : null,
        { label: "ISBN", value: getFrom("ISBN", parent0) },
        { label: "E-ISBN", value: getFrom("E-ISBN", parent0) },
        webUrl ? { label: "Web Adresi", value: webUrl } : null,
        // secondTitleField // Sadece varsa eklenecek
      ].filter(x => x && x.value);
    }

    // Konferans/Armağan → kitap türü gibi
    if (type.includes("konferans") || type.includes("armağan") || type.includes("armagan")) {
      const doiVal = first(getFrom("DOI", info), getFrom("DOI", parent0));
      return [
        doiVal ? { label: "DOI", value: `doi.org/${doiVal}` } : null,
        { label: "ISBN", value: first(getFrom("ISBN", info), getFrom("ISBN", parent0)) },
        { label: "E-ISBN", value: first(getFrom("E-ISBN", info), getFrom("E-ISBN", parent0)) },
        webUrl ? { label: "Web Adresi", value: webUrl } : null,
        // secondTitleField // Sadece varsa eklenecek
      ].filter(x => x && x.value);
    }
    return [];
  }

  // ---- Inline “Alt Bilgi” satırı: type-specific short fields ----
  function getTypeSpecificFields(pub) {
    const t = (pub.publicationtype || "").toLocaleLowerCase("tr-TR");
    if (t === "konferans ve armağan yazısı" || t === "konferans ve armağan yazisi") {
      const parent = pub.parentList?.[0];
      const parentInfo = parent?.publicationtypeInfo || [];
      const parentId = parent?.mysqlId;

      const konferansAdi = getFrom("Konferans Adı", parentInfo);
      const parentBaslik = getFrom("Başlık", parentInfo);
      const konferansLabel = first(konferansAdi, parentBaslik);

      if (!konferansLabel || !parentId) return [];

      const info = pub.publicationtypeInfo || [];
      const tInfo = pub.templateInfo || [];

      const fields = [
        {
          label: "Konferans",
          value: konferansLabel,
          href: buildPublicationPath({ title: konferansLabel, id: parentId }),
          onClick: () => openInNewTab(buildPublicationPath({ title: konferansLabel, id: parentId })),
        },
        { label: "Toplantı Yeri", value: first(getFrom("Yayın Yeri", info), getFrom("Yayın Yeri", parentInfo)) },
        // { label: "Toplantı Tarihi", value: first(getFrom("Toplantı tarihi", info), getFrom("Toplantı tarihi", parentInfo)) },
      ];

      const pubYear = getYearOnly(first(
        getFrom("Yayın Tarihi", tInfo),
        getFrom("Yayın Tarihi", info),
        getFrom("Yayın Tarihi", parentInfo)
      ));
      if (pubYear) fields.push({ label: "Tarih", value: pubYear });

      return fields.filter(f => f.value);
    }

    if (t === "konferans ve armağan" || t === "konferans ve armagan") {
      const info = pub.publicationtypeInfo || [];
      const tInfo = pub.templateInfo || [];

      // sadece publishers'a bak
      const yayinci = pub.publishers?.[0] || null;

      const yer = getFrom("Yayın Yeri", info);
      const yil = getYearOnly(first(getFrom("Yayın Tarihi", tInfo), getFrom("Yayın Tarihi", info)));
      const baski = first(getFrom("Baskı Bilgisi", tInfo), getFrom("Baskı Bilgisi", info));
      const cilt = first(getFrom("Cilt", info), getFrom("Cilt", tInfo));

      return [
        yayinci?.title && {
          label: "Yayıncı",
          // sorttitle varsa onu kullan, yoksa title'ı kullan
          value: (yayinci.sorttitle && yayinci.sorttitle.trim()) ? yayinci.sorttitle : yayinci.title,
          ...(yayinci.mysqlId
            ? {
              // href: buildPublisherPath({ title: yayinci.title, id: yayinci.mysqlId }),
              // onClick: () => openInNewTab(buildPublisherPath({ title: yayinci.title, id: yayinci.mysqlId })),
              href: `/publisher/publications/${yayinci.mysqlId}`,
              onClick: () => openInNewTab(`/publisher/publications/${yayinci.mysqlId}`),
            }
            : {})
        },
        yer && { label: "Yayın Yeri", value: yer },
        yil && { label: "Tarih", value: yil },
        baski && { label: "Baskı Bilgisi", value: baski },
      ].filter(Boolean);
    }



    const type = pub.publicationtype?.toLowerCase();

    // bazı türlerde field tıklamaları details değil PDF açsın istiyorsan burayı genişletebilirsin
    const pdfTypes = [
      "Akademik Makale",
      "Akademik Kitap Bölümü",
      "Konferans ve Armağan Yazısı",
      "Konferans ve Armağan",
      "Popüler Dergi Yazısı"
    ];
    if (pdfTypes.includes(pub.publicationtype)) {
      if (pub.publicationtype === "Akademik Kitap Bölümü") {
        return [
          {
            label: "Kitap adı",
            value: getFrom("Başlık", pub.parentList?.[0]?.publicationtypeInfo || []),
            href: buildPublicationPath({ title: getFrom("Başlık", pub.parentList?.[0]?.publicationtypeInfo || []), id: pub.parentList?.[0]?.mysqlId }),
            onclick: () => openInNewTab(buildPublicationPath({ title: getFrom("Başlık", pub.parentList?.[0]?.publicationtypeInfo || []), id: pub.parentList?.[0]?.mysqlId }))
          },
          {
            label: "Baskı bilgisi",
            value: first(
              getFrom("Baskı Bilgisi", pub.parentList?.[0]?.publicationtypeInfo || []),
              getFrom("Baskı Bilgisi", pub.publicationtypeInfo || [])
            ),
          },
          {
            label: "Tarih",
            value: getYearOnly(getFrom("Yayın Tarihi", pub.parentList?.[0]?.publicationtypeInfo || [])),
          }
        ].filter(f => f.value);
      }

      if (
        pub.publicationtype === "Akademik Makale" ||
        pub.publicationtype === "Popüler Dergi Yazısı"
      ) {
        // Dergi adı | Tarih | Cilt | Sayı
        const p0 = pub.parentList?.[0]?.publicationtypeInfo || [];
        const p1 = pub.parentList?.[1]?.publicationtypeInfo || [];
        const info = pub.publicationtypeInfo || [];
        const tInfo = pub.templateInfo || [];

        const journalOrTitle = first(
          getFrom("Dergi Adı", p1), getFrom("Başlık", p1),
          getFrom("Dergi Adı", p0), getFrom("Başlık", p0)
        );
        const year = getYearOnly(first(
          getFrom("Yayın Tarihi", tInfo),
          getFrom("Yayın Tarihi", info),
          getFrom("Yayın Tarihi", p0),
          getFrom("Yayın Tarihi", p1)
        ));
        const ciltRaw = first(getFrom("Cilt", p0), getFrom("Cilt", info));
        const sayiRaw = first(getFrom("Sayı", p0), getFrom("Sayı", info));
        const cilt = ciltRaw ? `Cilt ${ciltRaw}` : "";
        const sayi = sayiRaw ? `Sayı ${sayiRaw}` : "";

        const parentIdForLink = pub.parentList?.[1]?.mysqlId || pub.parentList?.[0]?.mysqlId;

        return [
          {
            label: "Dergi adı",
            value: journalOrTitle,
            href: buildPublicationPath({ title: journalOrTitle, id: parentIdForLink }),
            onClick: () => openInNewTab(buildPublicationPath({ title: journalOrTitle, id: parentIdForLink })),
          },
          year ? { label: "Tarih", value: year } : null,
          cilt ? { label: "Cilt", value: cilt } : null,
          sayi ? { label: "Sayı", value: sayi } : null,
        ].filter(Boolean);
      }
    }

    // default branches
    if (type === "akademik makale") {
      // Dergi adı | Tarih | Cilt | Sayı
      const p0 = pub.parentList?.[0]?.publicationtypeInfo || [];
      const p1 = pub.parentList?.[1]?.publicationtypeInfo || [];
      const info = pub.publicationtypeInfo || [];
      const tInfo = pub.templateInfo || [];

      const journalOrTitle = first(
        getFrom("Dergi Adı", p1), getFrom("Başlık", p1),
        getFrom("Dergi Adı", p0), getFrom("Başlık", p0)
      );
      const year = getYearOnly(first(
        getFrom("Yayın Tarihi", tInfo),
        getFrom("Yayın Tarihi", info),
        getFrom("Yayın Tarihi", p0),
        getFrom("Yayın Tarihi", p1)
      ));
      const ciltRaw = first(getFrom("Cilt", p0), getFrom("Cilt", info));
      const sayiRaw = first(getFrom("Sayı", p0), getFrom("Sayı", info));
      const cilt = ciltRaw ? `Cilt ${ciltRaw}` : "";
      const sayi = sayiRaw ? `Sayı ${sayiRaw}` : "";
      const parentIdForLink = pub.parentList?.[1]?.mysqlId || pub.parentList?.[0]?.mysqlId;

      return [
        {
          label: "Dergi adı",
          value: journalOrTitle,
          href: buildPublicationPath({ title: journalOrTitle, id: parentIdForLink }),
          onClick: () => openInNewTab(buildPublicationPath({ title: journalOrTitle, id: parentIdForLink })),
        },
        year ? { label: "Tarih", value: year } : null,
        cilt ? { label: "Cilt", value: cilt } : null,
        sayi ? { label: "Sayı", value: sayi } : null,
      ].filter(Boolean);
    }

    if (type === "akademik kitap") {
      return [
        {
          label: "Yayıncı",
          value:
            (pub.publishers?.[0]?.sorttitle && pub.publishers[0].sorttitle.trim()) ? pub.publishers[0].sorttitle :
              pub.publishers?.[0]?.title ||
              (pub.publicationtypeInfo || []).find(a => a.attributename?.toLowerCase() === "yayıncı")?.value?.[0]?.value?.[0] ||
              "",
          // href: buildPublisherPath({ title: pub.publishers?.[0]?.title, id: pub.publishers?.[0]?.mysqlId }),
          // onClick: () => openInNewTab(buildPublisherPath({ title: pub.publishers?.[0]?.title, id: pub.publishers?.[0]?.mysqlId })),
          href: `/publisher/publications/${pub.publishers?.[0]?.mysqlId}`,
          onClick: () => openInNewTab(`/publisher/publications/${pub.publishers?.[0]?.mysqlId}`),
        },
        {
          label: "Baskı Bilgisi",
          value: getFrom("Baskı Bilgisi", pub.publicationtypeInfo || []),
        },
        {
          label: "Yayın Tarihi",
          value: getYearOnly(first(
            getFrom("Yayın Tarihi", pub.publicationtypeInfo || []),
            getFrom("Yayın Tarihi", pub.templateInfo || [])
          )),
        }
      ].filter(f => f.value);
    }

    if (type === "dergi") {
      const publisher = pub.publishers?.[0];
      const periyot = getFrom("Periyot", pub.templateInfo || []);
      const sonYayinTarihi = getFrom("Son Yayınlandığı Tarih", pub.templateInfo || []);
      const yayinTarihi = getFrom("Yayın Tarihi", pub.templateInfo || []);
      const tarih = getYearOnly(first(sonYayinTarihi, yayinTarihi));
      return [
        publisher && {
          label: "Yayıncı",
          // sorttitle varsa onu kullan, yoksa title'ı kullan
          value: (publisher.sorttitle && publisher.sorttitle.trim()) ? publisher.sorttitle : publisher.title,
          // href: buildPublisherPath({ title: publisher.title, id: publisher.mysqlId }),
          // onClick: () => openInNewTab(buildPublisherPath({ title: publisher.title, id: publisher.mysqlId })),
          href: `/publisher/publications/${publisher.mysqlId}`,
          onClick: () => openInNewTab(`/publisher/publications/${publisher.mysqlId}`),
        },
        periyot && { label: "Baskı Bilgisi", value: periyot },
        tarih && { label: "Tarih", value: tarih }
      ].filter(Boolean);
    }

    if (type === "popüler dergi sayısı" || type === "populer dergi sayisi" || type === "akademik dergi sayısı" || type === "akademik dergi sayisi") {
      const parentInfo = pub.parentList?.[0]?.publicationtypeInfo || [];
      const dergiAdi = first(getFrom("Dergi Adı", parentInfo), getFrom("Başlık", parentInfo));
      const cilt = getFrom("Cilt", pub.publicationtypeInfo || []) || getFrom("Cilt", parentInfo);
      const sayi = getFrom("Sayı", pub.publicationtypeInfo || []) || getFrom("Sayı", parentInfo);
      const year = getYearOnly(first(
        getFrom("Yayın Tarihi", pub.templateInfo || []),
        getFrom("Yayın Tarihi", pub.publicationtypeInfo || []),
        getFrom("Yayın Tarihi", parentInfo)
      ));

      return [
        {
          label: "Dergi adı",
          value: dergiAdi,
          href: buildPublicationPath({ title: dergiAdi, id: pub.parentList?.[0]?.mysqlId }),
          onClick: () => openInNewTab(buildPublicationPath({ title: dergiAdi, id: pub.parentList?.[0]?.mysqlId })),
        },
        year ? { label: "Tarih", value: year } : null,
        cilt ? { label: "Cilt", value: `Cilt ${cilt}` } : null,
        sayi ? { label: "Sayı", value: `Sayı ${sayi}` } : null
      ].filter(f => f && f.value);
    }

    if (type === "popüler dergi yazısı" || type === "populer dergi yazısı") {
      const p0 = pub.parentList?.[0]?.publicationtypeInfo || [];
      const p1 = pub.parentList?.[1]?.publicationtypeInfo || [];
      const info = pub.publicationtypeInfo || [];
      const tInfo = pub.templateInfo || [];

      const journalOrTitle = first(
        getFrom("Dergi Adı", p1), getFrom("Başlık", p1),
        getFrom("Dergi Adı", p0), getFrom("Başlık", p0)
      );
      const year = getYearOnly(first(
        getFrom("Yayın Tarihi", tInfo),
        getFrom("Yayın Tarihi", info),
        getFrom("Yayın Tarihi", p0),
        getFrom("Yayın Tarihi", p1)
      ));
      const ciltRaw = first(getFrom("Cilt", p0), getFrom("Cilt", info));
      const sayiRaw = first(getFrom("Sayı", p0), getFrom("Sayı", info));
      const cilt = ciltRaw ? `Cilt ${ciltRaw}` : "";
      const sayi = sayiRaw ? `Sayı ${sayiRaw}` : "";
      const parentIdForLink = pub.parentList?.[1]?.mysqlId || pub.parentList?.[0]?.mysqlId;

      return [
        {
          label: "Dergi adı",
          value: journalOrTitle,
          href: buildPublicationPath({ title: journalOrTitle, id: parentIdForLink }),
          onClick: () => openInNewTab(buildPublicationPath({ title: journalOrTitle, id: parentIdForLink })),
        },
        year ? { label: "Tarih", value: year } : null,
        cilt ? { label: "Cilt", value: cilt } : null,
        sayi ? { label: "Sayı", value: sayi } : null,
      ].filter(Boolean);
    }

    if (type === "popüler kitap" || type === "populer kitap") {
      return [
        pub.publishers?.[0]?.title && {
          label: "Yayıncı",
          // sorttitle varsa onu kullan, yoksa title'ı kullan
          value: (pub.publishers[0].sorttitle && pub.publishers[0].sorttitle.trim()) ? pub.publishers[0].sorttitle : pub.publishers[0].title,
          // href: `/publisher-detail/${pub.publishers?.[0].mysqlId}`,
          // onClick: () => openInNewTab(`/publisher-detail/${pub.publishers?.[0].mysqlId}`),
          href: `/publisher/publications/${pub.publishers?.[0].mysqlId}`,
          onClick: () => openInNewTab(`/publisher/publications/${pub.publishers?.[0].mysqlId}`),
        },
        {
          label: "Baskı Bilgisi",
          value: getFrom("Baskı Bilgisi", pub.publicationtypeInfo || [])
        },
        {
          label: "Yayın Tarihi",
          value: getYearOnly(first(
            getFrom("Yayın Tarihi", pub.publicationtypeInfo || []),
            getFrom("Yayın Tarihi", pub.templateInfo || [])
          ))
        }
      ].filter(Boolean);
    }

    return [];
  }

  const rawSummary =
    pub.publicationtypeInfo?.find(a => a.attributename === "Özet")?.value?.[0]?.value?.[0] ||
    pub.templateInfo?.find(a => a.attributename === "Özet")?.value?.[0]?.value?.[0] ||
    pub.parentList?.[0]?.publicationtypeInfo?.find(a => a.attributename === "Açıklama")?.value?.[0]?.value?.[0] ||
    "";

  const summaryPlain = htmlToPlain(rawSummary);

  return (
    <div
      className={`card mb-0 mx-2 border-0 bg-default-color-3 rounded-3 mb-3${isHovered ? ' shadow-sm' : ''}`}
      style={{ position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* lock icon */}
      <FeatherIcon
        icon={!isAccessible ? "lock" : "unlock"}
        color={!isAccessible ? "#b0b0b0" : "#14A077"}
        size={18}
        title={!isAccessible ? "Kısıtlı Erişim" : "Açık Erişim"}
        style={{ position: 'absolute', top: 26, right: 68, zIndex: 2 }}
      />
      <div className="card-body pb-0 ">
        {/* row */}
        <div className="row align-items-start g-2">
          {/* checkbox */}
          <div className="col-auto d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input"
              checked={selected}
              onChange={onSelect}
            />
          </div>
          {/* content */}
          <div className="col small ">
            {/* type badge */}
            <span
              className="me-2 mb-1 d-inline-block text-darkfume"
              style={{ fontSize: '0.95em' }}
            >
              {normalizeTypeDisplay(type)}
            </span>
            {/* title */}
            <div className="mb-1 mt-1" style={{ lineHeight: 1.25 }}>
              <a
                href={titleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-newdarkfume d-inline"           // sadece metin kadar tıklanır
                style={{ fontSize: "1rem", textDecoration: "none", cursor: "pointer" }}
                title={htmlToPlain(title)}                      // toolip düz metin
                onClick={(e) => {
                  const sel = window.getSelection && window.getSelection().toString();
                  if (sel) { e.preventDefault(); e.stopPropagation(); } // kopyalama güvenli
                }}
                onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
                dangerouslySetInnerHTML={{ __html: title }}
              />
              {showCilt && (
                <a href={titleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ms-1 text-newdarkfume"
                  style={{
                    fontSize: "1rem",
                    textDecoration: "none",
                    cursor: "pointer"
                  }}>
                  ({`Cilt ${ciltValue}`})
                </a>
              )}
            </div>
            {/* authors */}
            {authors.length > 0 && (
              <div className="mb-1 small" style={{ fontSize: '1em' }}>
                {persons.map((p, i) => {
                  const fullName = [p?.name, p?.surname].filter(Boolean).join(" ").trim();
                  const label = mode === "editor" ? `${fullName} (ed.)` : fullName;

                  return (
                    <span key={p?.mysqlId || `${fullName}-${i}`}>
                      <a
                        href={buildAuthorPath({ name: p.name, surname: p.surname, id: p.mysqlId })}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ cursor: "pointer", textDecoration: "none" }}
                        className="text-darkfume"
                        onMouseOver={e => e.currentTarget.style.textDecoration = "underline"}
                        onMouseOut={e => e.currentTarget.style.textDecoration = "none"}
                        dangerouslySetInnerHTML={{ __html: label }}
                      />
                      {i < persons.length - 1 ? ', ' : ''}
                    </span>
                  );
                })}
              </div>
            )}
            {/* inline meta */}
            <div className="mb-2 text-darkfume small" style={{ fontSize: '0.95em' }}>
              {getTypeSpecificFields(pub)
                .map((field, i, arr) =>
                  field.href && field.value ? (
                    <a
                      key={i}
                      href={field.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: "pointer", textDecoration: "none", padding: "0 1px" }}
                      className="text-darkfume"
                      onClick={e => {
                        if (field.onClick) {
                          e.preventDefault();
                          field.onClick();
                        }
                      }}
                      onMouseOver={e => e.currentTarget.style.textDecoration = "underline"}
                      onMouseOut={e => e.currentTarget.style.textDecoration = "none"}
                    >
                      {field.value}
                      {i < arr.length - 1 ? ' | ' : ''}
                    </a>
                  ) : field.value ? (
                    <span key={i}>
                      {field.value}
                      {i < arr.length - 1 ? ' | ' : ''}
                    </span>
                  ) : null
                )
              }
            </div>
            {/* tags */}
            <div className="mb-2">
              {tags.map((tag, i) => {
                const displayTag = toTitleCase(tag);
                return (
                  <span
                    key={i}
                    className="badge bg-light border me-1 small text-darkfume"
                    style={{ fontSize: "0.90em", fontWeight: 500, border: "1px solid #e0e2f5", cursor: "pointer" }}
                    dangerouslySetInnerHTML={{ __html: displayTag }}
                    onClick={() => onKeywordClick && onKeywordClick(tag)}
                    title="Bu anahtar kelimeyle ara"
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* action bar */}
        <div className="d-flex flex-wrap flex-md-nowrap align-items-center justify-content-between gap-2 py-1 mb-3 ps-2">
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn-sm ms-2 text-blue border-0 bg-transparent"
              style={{ fontSize: "0.80rem" }}
              onClick={onToggleSummary}
              aria-expanded={isSummaryOpen}
            >
              Özet<span style={{ verticalAlign: 'top', marginLeft: 2 }}>{isSummaryOpen ? '▲' : '▼'}</span>
            </button>
            <button
              className="btn-sm text-blue border-0 bg-transparent"
              style={{ fontSize: "0.80rem" }}
              onClick={() => handleOpenModal(pub)}
            >
              Alıntıla
            </button>

            {pub.pdffile && (
              <OverlayTrigger
                placement="right"
                delay={{ show: 150, hide: 0 }}
                overlay={
                  <Tooltip
                    id={`pdf-tip-${pub.mysqlId}`}
                    style={{ fontSize: "0.78rem", lineHeight: 1.1 }}
                  >
                    PDF’i görüntüle
                  </Tooltip>
                }
              >
                <button
                  className="btn-sm border-0 bg-transparent d-inline-flex align-items-center"
                  style={{ fontSize: "0.80rem", color: "#cb1407ff" }}
                  onClick={() => window.open(pdfLink, "_blank", "noopener,noreferrer")}
                  title="PDF’i görüntüle"
                  aria-label="PDF’i görüntüle"
                >
                  <PdfAcrobatIcon size={16} />
                </button>
              </OverlayTrigger>

            )}
          </div>

          <div style={{ minWidth: "40px", marginRight: "20px" }} className="d-flex justify-content-end align-items-center">
            <button
              className="btn-sm m-0 border-0 bg-transparent text-fume d-flex align-items-center"
              style={{ fontSize: "0.80rem" }}
              disabled={saving}
              aria-disabled={saving}
              onMouseEnter={() => setIsSaveHovered(true)}
              onMouseLeave={() => setIsSaveHovered(false)}
              title={saved ? (isSaveHovered ? "Kaydedilenlerden sil" : "Kaydedildi") : "Kaydet"}
              aria-label={saved ? (isSaveHovered ? "Kaydedilenlerden sil" : "Kaydedildi") : "Kaydet"}
              onClick={async () => {
                if (!auth?.token) {
                  toast.warning("Kaydetmek için önce giriş yapmalısınız.");
                  return;
                }
                if (savingRef.current) return;
                savingRef.current = true;
                setSaving(true);
                try {
                  if (saved) {
                    if (typeof onRemoveSaved === "function") {
                      onRemoveSaved(pub.mysqlId);
                    }
                  } else {
                    await onAddToLibrary(pub);
                  }
                } finally {
                  setTimeout(() => {
                    savingRef.current = false;
                    setSaving(false);
                  }, 800);
                }
              }}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                  <span className="d-none d-md-inline">Kaydediliyor…</span>
                </>
              ) : (
                <>
                  <i
                    className={`fa-bookmark${favoriteSavedIds.includes(String(pub.mysqlId))
                      ? " text-primary fa-solid"
                      : " fa-regular text-fume"
                      }`}
                    style={{ fontSize: "0.85rem" }}
                  />
                  <span className="d-none d-md-inline ms-1">
                    {saved ? (isSaveHovered ? "Kaydedilenlerden Sil" : "Kaydedildi") : "Kaydet"}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* summary panel */}
        <div
          ref={journalInfoRef}
          className={`journal-info-anim${isSummaryOpen ? " open" : ""}`}
          style={{
            borderRadius: "0.75rem",
            padding: isSummaryOpen ? "1rem" : "0 1rem",
            fontSize: '0.95em',
            marginTop: isSummaryOpen ? "0.5rem" : 0,
          }}
        >
          <strong className="ms-1">Özet:</strong>
          <div
            className="mb-3 mt-1 p-2 rounded-3 border"
            style={{
              maxHeight: SUMMARY_MAX_PX,
              overflowY: "auto",
              lineHeight: 1.4,
              scrollbarWidth: "thin",
            }}
            onWheel={handleSummaryWheel}
          >
            <div className="text-darkfume" dangerouslySetInnerHTML={{ __html: summaryPlain || "Bilgi yok." }} />
          </div>

          <div className="row small">
            {getSummaryFields(pub).map((f, i) => (
              <div className="col-md-6 mb-1" key={i}>
                <b>{f.label}:</b>{" "}
                {/* Değer bir URL mi kontrol et */}
                {String(f.value).startsWith("doi.org/") || String(f.value).startsWith("http") ? (
                  // Eğer URL ise, <a href> etiketi içinde göster
                  <a href={`https://${String(f.value).replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer">
                    {f.value}
                  </a>
                ) : (
                  // Eğer düz metin (veya HTML içeren metin) ise, dangerouslySetInnerHTML ile göster
                  <span dangerouslySetInnerHTML={{ __html: f.value }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CiteModal
        show={showModal}
        onHide={handleCloseModal}
        selectedFormat={selectedFormat}
        setSelectedFormat={setSelectedFormat}
        renderCitation={renderCitation}
      />
    </div>
  );
};

export default PublicationCard;
